import React, { useMemo } from 'react';
import { useIntl } from 'umi';
import { transformTrendData } from '@/pages/strategic/components/MetricsBoardVertical/transformTrendData';
import { formatPercentage } from '@/pages/strategic/utils/common';
import MetricsBoardVertical from '@/pages/strategic/components/MetricsBoardVertical';
import Loading from '@intelligence/components/Loading';
import { getMetricDisplayName, findMetricInfo, getComparisonLabels, type MetricTextInfo } from '../../utils/metricUtils';
import { METRIC_SPECIAL_COMPARISON_LABELS } from '../../config';

// Component to display dashboard metric data (receives pre-fetched data)
export interface IDashboardMetricCardProps {
    metric: { metrics_name: string; granularity?: 'daily' | 'weekly' | 'monthly' };
    gameCode: string;
    locale: string;
    isEditing: boolean;
    onHeaderClick: () => void;
    onEdit: () => void;
    onDelete: () => void;
    metricTextInfo?: MetricTextInfo[];
    batchedMetricData?: any; // Pre-fetched metric data from parent
    batchedTrendList?: any[]; // Pre-fetched trend list from parent
    isLoading?: boolean; // Loading state from parent
}

const DashboardMetricCard: React.FC<IDashboardMetricCardProps> = React.memo(({
    metric,
    gameCode,
    locale,
    isEditing,
    onHeaderClick,
    onEdit,
    onDelete,
    metricTextInfo = [],
    batchedMetricData = null,
    batchedTrendList = [],
    isLoading = false
}) => {
    const { formatMessage } = useIntl();
    const isEn = locale === 'en-US';

    // Transform trend data for chart
    const trendData = useMemo(() => {
        if (!batchedTrendList || batchedTrendList.length === 0 || !metric.metrics_name) return undefined;

        // Map granularity to dateDimension: 'daily' → 'DAY', 'weekly' → 'WEEK', 'monthly' → 'MONTH'
        const granularity = metric.granularity || 'daily';
        const dateDimension = granularity === 'daily' 
            ? 'DAY' 
            : granularity === 'weekly' 
                ? 'WEEK' 
                : 'MONTH';

        const { xAxisData, series } = transformTrendData(
            batchedTrendList,
            metric.metrics_name,
            dateDimension,
            formatMessage
        );

        return {
            xAxisData,
            series,
            metrics_name: metric.metrics_name,
            granularity: metric.granularity, // Pass granularity for tooltip formatting
        };
    }, [batchedTrendList, metric.metrics_name, metric.granularity, formatMessage]);

    // Parse the value from API response
    const rawValue = batchedMetricData?.value;
    const displayValue = typeof rawValue === 'number'
        ? rawValue
        : typeof rawValue === 'string'
            ? (isNaN(parseFloat(rawValue)) ? null : parseFloat(rawValue))
            : null;
    
    // Special handling for metrics that use 'change' instead of 'dod'
    const usesChangeValue = metric.metrics_name in METRIC_SPECIAL_COMPARISON_LABELS;
    const displayChange = usesChangeValue 
        ? batchedMetricData?.change?.show_value 
        : batchedMetricData?.dod?.show_value;
    const displaySwlmChange = batchedMetricData?.sdlw?.show_value;
    const isNoAccess = Boolean(batchedMetricData?.noAccess);

    // Determine textList labels based on granularity using shared utility
    // Special metrics like full_game_asp use 'change' value instead of dod/sdlw
    const granularity = metric.granularity || 'daily';
    const textList = getComparisonLabels(
        granularity, 
        displayChange ?? null, 
        displaySwlmChange ?? null, 
        formatPercentage,
        metric.metrics_name // Pass metric key for special handling
    );

    // Find metricInfo with granularity matching for active_users using shared utility
    const metricInfo = findMetricInfo(metric.metrics_name, metricTextInfo, granularity);
    const formatter = metricInfo?.formatter;
    const unit = metricInfo?.unit;
    // const explain = isEn ? metricInfo?.explainEn : metricInfo?.explainCn;
    const explain = metricInfo?.explainEn; // temporary disable chinese name

    // Check if metricTextInfo is ready
    // We need to wait for metricTextInfo to be populated before rendering
    // If metricTextInfo is empty, it means it hasn't been fetched yet (should show loading)
    // Once metricTextInfo has data (even if our specific metric isn't found), we can render
    const isMetricTextInfoReady = metricTextInfo.length > 0 || metricInfo !== undefined;

    // Show loading if either metric data is loading OR metricTextInfo hasn't been fetched yet
    if (isLoading || !isMetricTextInfoReady) {
        return (
            <div className="dbs-sortable-item-2-6">
                <Loading classNameValue="dbs-loading" />
            </div>
        );
    }

    return (
        <div className="dbs-sortable-item-2-6">
            <MetricsBoardVertical
                title={getMetricDisplayName(metric.metrics_name, locale, metricTextInfo, granularity)}
                num={displayValue}
                formatter={formatter}
                unit={unit}
                explain={explain}
                textList={textList}
                trendData={trendData}
                onHeaderClick={onHeaderClick}
                mode={isEditing ? 'edit' : 'normal'}
                onEdit={onEdit}
                onDelete={onDelete}
                noAccess={isNoAccess}
            />
        </div>
    );
}, (prevProps, nextProps) => {
    // Only re-render if metric name, gameCode, isEditing, locale, metricTextInfo, or data changes
    const prevMetricTextInfoLength = prevProps.metricTextInfo?.length || 0;
    const nextMetricTextInfoLength = nextProps.metricTextInfo?.length || 0;
    const metricTextInfoChanged = prevMetricTextInfoLength !== nextMetricTextInfoLength ||
        (prevMetricTextInfoLength > 0 && nextMetricTextInfoLength > 0 &&
            JSON.stringify(prevProps.metricTextInfo) !== JSON.stringify(nextProps.metricTextInfo));

    const dataChanged = JSON.stringify(prevProps.batchedMetricData) !== JSON.stringify(nextProps.batchedMetricData) ||
        JSON.stringify(prevProps.batchedTrendList) !== JSON.stringify(nextProps.batchedTrendList);

    return (
        prevProps.metric.metrics_name === nextProps.metric.metrics_name &&
        prevProps.gameCode === nextProps.gameCode &&
        prevProps.isEditing === nextProps.isEditing &&
        prevProps.locale === nextProps.locale &&
        prevProps.isLoading === nextProps.isLoading &&
        !metricTextInfoChanged &&
        !dataChanged
    );
});

export default DashboardMetricCard;
